import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ProjectCard from './ProjectCard';
import justyolImg from '../assets/images/justyol.webp';
import MaykanaImg from '../assets/images/maykana.webp';
import vosmosImg from '../assets/images/vosmos.webp';
import atayaImg from '../assets/images/ataya.webp';
import '../styles/Projects.css';

gsap.registerPlugin(ScrollTrigger);

const projects = [
  {
    id: 'ataya',
    title: 'Atayaa Al Rahman Foundation & ERP',
    category: ['ERP & Frappe', 'Web & E-Commerce'],
    description:
      'Charity foundation web portal focused on empowering individuals with physical disabilities. Architected with React (Vite) and an ERPNext backend to automate online donation flows, member registration, bilingual content, and live fund tracking.',
    outcome: 'Automated digital donor receipts, member intake, and real-time donation analytics.',
    image: atayaImg,
    link: 'https://atayaalrahman.org/',
    tags: ['React', 'ERPNext', 'Python', 'Vite', 'Tailwind CSS']
  },
  {
    id: 'justyol',
    title: 'Justyol - Enterprise E-Commerce & ERPNext Pipeline',
    category: ['ERP & Frappe', 'APIs & Integrations'],
    description:
      'Engineered a bi-directional synchronization pipeline connecting Shopify with ERPNext for Turkish e-commerce platform Justyol. Integrated Aramex and Cathedis logistics APIs, Turkish Tax Authority e-invoicing compliance, and Meta WhatsApp API order notifications.',
    outcome: 'Real-time order synchronization, automated dispatch workflows, and full Turkish tax compliance.',
    image: justyolImg,
    link: 'https://justyol.com',
    tags: ['ERPNext', 'Frappe', 'Shopify API', 'WhatsApp API', 'Turkish Tax E-Invoicing']
  },
  {
    id: 'vosmos',
    title: 'Vosmos Clothing Store',
    category: ['Web & E-Commerce'],
    description:
      'Full-featured Django e-commerce platform built for fashion retail. Features customized product attributes, persistent session cart, guest checkout, automated email notification pipeline, and a comprehensive admin inventory dashboard.',
    outcome: 'Sub-100ms response time on catalog search, persistent carts, and streamlined checkout.',
    image: vosmosImg,
    link: 'https://vosmos.pythonanywhere.com',
    tags: ['Django', 'Python', 'E-Commerce', 'PostgreSQL', 'CSS3']
  },
  {
    id: 'zatca',
    title: 'Saudi ZATCA Phase 2 E-Invoicing on ERPNext',
    category: ['ERP & Frappe', 'APIs & Integrations'],
    description:
      'Led the implementation of Saudi Tax Authority (ZATCA) Phase 2 requirements for ERPNext at Appy Innovate. Built automated cryptographic stamping, UBL 2.1 compliant XML invoice generation, QR code rendering, and bulk invoice transmission with automated error recovery.',
    outcome: 'Accelerated customer migration timelines by 10x with 100% tax compliance across thousands of invoices.',
    image: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    link: null,
    tags: ['ERPNext', 'Frappe', 'Python', 'ZATCA Phase 2', 'Tax Compliance']
  },
  {
    id: 'triple-gates',
    title: 'Triple Gates - Frappe & ERPNext UI/UX Revamp',
    category: ['ERP & Frappe'],
    description:
      'Designed and developed a modular custom Frappe application to revamp the platform’s visual identity and user interface ergonomics. Extended core Frappe components with modern JavaScript and responsive CSS to deliver frictionless day-to-day business operations.',
    outcome: 'Streamlined employee navigation and reduced daily administrative task time.',
    image: MaykanaImg,
    link: null,
    tags: ['Frappe Framework', 'ERPNext', 'JavaScript', 'UI/UX Design', 'Azure DevOps']
  },
  {
    id: 'saas-automation',
    title: 'ERP SaaS Automated Multi-Tenant Provisioning',
    category: ['ERP & Frappe'],
    description:
      'Engineered an automated multi-tenant ERP provisioning system using Python and Frappe Bench. Automates client self-service registration, isolated database instantiation, and instant deployment of ready-to-use ERPNext sites without manual sysadmin intervention.',
    outcome: 'Instant zero-touch site provisioning from user signup to live tenant deployment.',
    image: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    link: null,
    tags: ['Python', 'Frappe Bench', 'ERPNext', 'Docker', 'Automation']
  },
  {
    id: 'camelo',
    title: 'Camelo - Airport Transportation Backend (AMK LLC)',
    category: ['ERP & Frappe', 'APIs & Integrations'],
    description:
      'Backend architecture and microservices built on ERPNext powering an on-demand shared airport transportation system. Integrates payment gateway processing, Firebase Cloud Messaging (FCM) for real-time driver/passenger notifications, webhooks, and REST APIs for Flutter mobile apps.',
    outcome: 'High-concurrency ride dispatch APIs, automated passenger billing, and live push notifications.',
    image: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    link: null,
    tags: ['ERPNext', 'Frappe HR', 'Python', 'Firebase', 'REST APIs', 'Payment Gateway']
  }
];

const categories = ['All', 'ERP & Frappe', 'Web & E-Commerce', 'APIs & Integrations'];

const Projects = () => {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const cardsRef = useRef(null);
  const [activeCategory, setActiveCategory] = useState('All');

  const filteredProjects = activeCategory === 'All'
    ? projects
    : projects.filter((p) => p.category.includes(activeCategory));

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(titleRef.current, {
        y: 50,
        autoAlpha: 0,
        duration: 0.8,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 80%',
          toggleActions: 'play none none none'
        },
        clearProps: 'transform,opacity,visibility'
      });

      if (cardsRef.current) {
        gsap.from(cardsRef.current.children, {
          y: 60,
          autoAlpha: 0,
          scale: 0.94,
          stagger: 0.12,
          duration: 0.7,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: cardsRef.current,
            start: 'top 85%',
            toggleActions: 'play none none none'
          },
          clearProps: 'transform,opacity,visibility'
        });
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  // Animate cards on category filter change and refresh ScrollTrigger for dynamic page heights
  useEffect(() => {
    if (cardsRef.current) {
      gsap.fromTo(
        cardsRef.current.children,
        { opacity: 0, y: 25 },
        {
          opacity: 1,
          y: 0,
          stagger: 0.08,
          duration: 0.4,
          ease: 'power2.out',
          clearProps: 'transform,opacity',
          onComplete: () => {
            ScrollTrigger.refresh();
          }
        }
      );
      // Immediate refresh for layout shifts
      ScrollTrigger.refresh();
    }
  }, [activeCategory]);

  return (
    <section ref={sectionRef} className="projects" id="projects">
      <div className="projects-container">
        <div ref={titleRef} className="projects-header">
          <span className="projects-label">Proven Track Record</span>
          <h2 className="projects-title">Featured Projects</h2>
          <p className="projects-subtitle">
            Enterprise ERP systems, custom Frappe integrations, and full-stack web applications built for real production workloads.
          </p>

          {/* Category Filter Pills */}
          <div className="projects-filter-bar">
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                className={`projects-filter-btn ${activeCategory === cat ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat)}
              >
                {cat}
                <span className="projects-filter-count">
                  {cat === 'All'
                    ? projects.length
                    : projects.filter((p) => p.category.includes(cat)).length}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div ref={cardsRef} className="projects-grid">
          {filteredProjects.map((project, index) => (
            <ProjectCard
              key={project.id}
              {...project}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
